import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { flushSync } from 'react-dom';
import { Button, Select, Spin, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import Utils from '@utils/utils';
import { uploadFlowImage } from '../../execution';
import styles from '../MaterialHD/index.module.scss';

const COUNT_OPTIONS = Array.from({ length: 4 }, (_, index) => ({
    value: index + 1,
    label: `${index + 1}张`,
}));
const IMAGE_UPSCALE_UNIT_COST = 10;
const DEFAULT_GENERATE_COUNT = 4;
const DEFAULT_MODE = 0;
const LINKED_IMAGE_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-opshub/FreeCanvas/node-image.svg';
const FLOW_LINK_PLACEHOLDER_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-opshub/FreeCanvas/node-image.svg';
const beanIcon = require('@assets/images/arcbean3.png');

const isSameValue = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const buildInitialUploads = () => ({
    source: '',
});

const UPLOAD_CARDS = [
    { key: 'source', title: '输入图', placeholder: '输入图' },
];

const ImageUpscalePanel = ({
    nodeId,
    nodeData,
    onChange,
    disabled = false,
}) => {
    const fileInputRefs = useRef({});
    const panelState = nodeData?.panelState || {};
    const inputParams = nodeData?.inputParams || {};
    const [uploads, setUploads] = useState(() => ({
        ...buildInitialUploads(),
        ...(panelState.uploads || {}),
    }));
    const [uploadingSlotKey, setUploadingSlotKey] = useState('');
    const [generateCount, setGenerateCount] = useState(panelState.num ?? inputParams.num ?? DEFAULT_GENERATE_COUNT);
    const lastSubmittedRef = useRef('');
    const skipNextSubmitRef = useRef(false);
    const lastExternalSyncSignatureRef = useRef('');
    const linkedSlotSources = useMemo(() => (
        nodeData?.panelState?.linkedSlotSources
        && typeof nodeData.panelState.linkedSlotSources === 'object'
        && !Array.isArray(nodeData.panelState.linkedSlotSources)
            ? nodeData.panelState.linkedSlotSources
            : {}
    ), [nodeData?.panelState?.linkedSlotSources]);
    const costBean = useMemo(() => generateCount * IMAGE_UPSCALE_UNIT_COST, [generateCount]);

    useEffect(() => {
        const nextUploads = {
            ...buildInitialUploads(),
            ...(panelState.uploads || {}),
        };
        const nextGenerateCount = panelState.num ?? inputParams.num ?? DEFAULT_GENERATE_COUNT;
        const externalSyncSignature = JSON.stringify({
            uploads: nextUploads,
            generateCount: nextGenerateCount,
        });

        if (lastExternalSyncSignatureRef.current === externalSyncSignature) {
            return;
        }
        lastExternalSyncSignatureRef.current = externalSyncSignature;

        const shouldSyncFromNode = (
            !isSameValue(uploads, nextUploads)
            || generateCount !== nextGenerateCount
        );

        if (shouldSyncFromNode) {
            skipNextSubmitRef.current = true;
        }

        setUploads((prev) => (isSameValue(prev, nextUploads) ? prev : nextUploads));
        setGenerateCount((prev) => (prev === nextGenerateCount ? prev : nextGenerateCount));
    }, [
        generateCount,
        inputParams.num,
        panelState.num,
        panelState.uploads,
        uploads,
    ]);

    const handleUploadChange = useCallback((slotKey, nextValue) => {
        setUploads((prev) => ({
            ...prev,
            [slotKey]: nextValue,
        }));
    }, []);

    const handleUploadedValue = useCallback((slotKey, nextValue) => {
        const linkedState = linkedSlotSources?.[slotKey];
        if (linkedState?.edgeId) {
            skipNextSubmitRef.current = true;
            nodeData?.onReplaceLinkedInput?.(nodeId, slotKey, linkedState, nextValue);
        }
        handleUploadChange(slotKey, nextValue);
    }, [handleUploadChange, linkedSlotSources, nodeData, nodeId]);

    const handleUploadDragOver = useCallback((slotKey, event) => {
        const hasFile = Array.from(event.dataTransfer?.types || []).includes('Files');
        if (!hasFile) return;

        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = 'copy';
    }, []);

    const handleUploadDrop = useCallback(async (slotKey, event) => {
        if (uploadingSlotKey) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        const files = Array.from(event.dataTransfer?.files || []);
        const imageFile = files.find((file) => file?.type?.startsWith('image/'));
        if (!imageFile) return;

        event.preventDefault();
        event.stopPropagation();
        flushSync(() => setUploadingSlotKey(slotKey));
        try {
            const fileUrl = await uploadFlowImage(imageFile);
            if (fileUrl) {
                handleUploadedValue(slotKey, fileUrl);
            }
        } catch (error) {
            message.error('上传失败，请稍后重试');
        } finally {
            setUploadingSlotKey('');
        }
    }, [handleUploadedValue, uploadingSlotKey]);

    const handleOpenLocalPicker = useCallback((slotKey, event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (disabled || uploadingSlotKey) return;

        const inputElement = fileInputRefs.current?.[slotKey];
        if (!inputElement) return;

        if (typeof inputElement.showPicker === 'function') {
            try {
                inputElement.showPicker();
                return;
            } catch (error) {
                // Fall back to click() when showPicker is unavailable or blocked.
            }
        }

        inputElement.click();
    }, [disabled, uploadingSlotKey]);

    const handleFileChange = useCallback(async (slotKey, event) => {
        const file = event.target.files?.[0];
        event.target.value = '';

        if (!file || disabled || uploadingSlotKey) return;

        flushSync(() => setUploadingSlotKey(slotKey));
        try {
            const fileUrl = await uploadFlowImage(file);
            if (fileUrl) {
                handleUploadedValue(slotKey, fileUrl);
            }
        } catch (error) {
            message.error('上传失败，请稍后重试');
        } finally {
            setUploadingSlotKey('');
        }
    }, [disabled, handleUploadedValue, uploadingSlotKey]);

    const handleRemoveUpload = useCallback((slotKey, event) => {
        event.preventDefault();
        event.stopPropagation();

        const linkedState = linkedSlotSources?.[slotKey];
        if (linkedState?.edgeId) {
            nodeData?.onRemoveLinkedInput?.(nodeId, slotKey, linkedState);
            skipNextSubmitRef.current = true;
        }

        handleUploadChange(slotKey, '');
    }, [handleUploadChange, linkedSlotSources, nodeData, nodeId]);

    const handleGenerate = useCallback(() => {
        if (!uploads.source) {
            message.warning('请先上传输入图');
            return;
        }

        nodeData?.onExecute?.(nodeId, { count: generateCount });
        nodeData?.onGenerateComplete?.(nodeId);
    }, [generateCount, nodeData, nodeId, uploads.source]);

    useEffect(() => {
        if (!nodeId || typeof onChange !== 'function') return;
        if (skipNextSubmitRef.current) {
            skipNextSubmitRef.current = false;
            return;
        }

        const currentLinkedSlotSources = (
            nodeData?.panelState?.linkedSlotSources
            && typeof nodeData.panelState.linkedSlotSources === 'object'
            && !Array.isArray(nodeData.panelState.linkedSlotSources)
        )
            ? nodeData.panelState.linkedSlotSources
            : {};
        const nextLinkedSlotSources = Object.fromEntries(
            Object.entries(currentLinkedSlotSources).filter(([slotKey, linkedState]) => (
                ['source'].includes(slotKey)
                && (
                    (!!linkedState?.edgeId && uploads?.[slotKey] !== '')
                    || (uploads?.[slotKey] && uploads[slotKey] === (linkedState?.url || ''))
                )
            )),
        );

        const nextPatch = {
            inputParams: {
                srcImage: uploads.source || '',
                mode: DEFAULT_MODE,
                num: generateCount,
            },
            panelState: {
                uploads,
                linkedSlotSources: nextLinkedSlotSources,
                extraSwitchValues: { mode: DEFAULT_MODE },
                num: generateCount,
            },
        };
        const nextSignature = JSON.stringify(nextPatch);

        if (
            lastSubmittedRef.current === nextSignature
            || (
                isSameValue(nodeData?.inputParams || {}, nextPatch.inputParams)
                && isSameValue(nodeData?.panelState || {}, nextPatch.panelState)
            )
        ) {
            lastSubmittedRef.current = nextSignature;
            return;
        }

        lastSubmittedRef.current = nextSignature;
        onChange(nodeId, nextPatch);
    }, [
        generateCount,
        nodeData?.inputParams,
        nodeData?.panelState,
        nodeId,
        onChange,
        uploads,
    ]);

    const isGenerating = String(
        nodeData?.latestExecution?.status
        || nodeData?.executionStatus
        || 'idle',
    ).toLowerCase() === 'processing';
    const canGenerate = !disabled && !isGenerating && (
        !!uploads.source || !!linkedSlotSources?.source?.edgeId
    );

    return (
        <>
            <div className={`${styles.panel} ${disabled ? styles.panelDisabled : ''}`}>
                <div className={styles.uploadRow}>
                    {UPLOAD_CARDS.map((card) => {
                        const linkedState = linkedSlotSources?.[card.key];
                        const previewValue = uploads[card.key] || linkedState?.url || '';
                        const isLinkedInput = !!linkedState?.edgeId;
                        const isLinkedPlaceholder = previewValue === FLOW_LINK_PLACEHOLDER_ICON;
                        const hasResolvedLinkedPreview = isLinkedInput && !!previewValue && !isLinkedPlaceholder;
                        const previewUrl = (
                            previewValue
                            && previewValue !== FLOW_LINK_PLACEHOLDER_ICON
                            && previewValue !== LINKED_IMAGE_ICON
                        ) ? Utils.getImgThumbnail(previewValue) : '';

                        return (
                            <div key={card.key} className={styles.cardColumn}>
                                <div
                                    role="button"
                                    tabIndex={0}
                                    className={styles.uploadCard}
                                    onClick={(event) => handleOpenLocalPicker(card.key, event)}
                                    onMouseDown={(event) => event.stopPropagation()}
                                    onDragOver={(event) => handleUploadDragOver(card.key, event)}
                                    onDrop={(event) => handleUploadDrop(card.key, event)}
                                >
                                    {previewUrl ? (
                                        <>
                                            <img src={previewUrl} alt={card.title} className={styles.preview} draggable={false} />
                                            <div className={styles.previewTitle}>{card.placeholder}</div>
                                        </>
                                    ) : isLinkedPlaceholder ? (
                                        <div className={styles.linkedPlaceholder}>
                                            <img
                                                src={FLOW_LINK_PLACEHOLDER_ICON}
                                                alt=""
                                                className={styles.linkedPlaceholderIcon}
                                                draggable={false}
                                            />
                                            <span className={styles.linkedPlaceholderLabel}>
                                                {linkedState?.imageSequenceLabel || ''}
                                            </span>
                                        </div>
                                    ) : hasResolvedLinkedPreview ? (
                                        <>
                                            <img src={previewUrl} alt={card.title} className={styles.preview} draggable={false} />
                                            <div className={styles.previewTitle}>{card.placeholder}</div>
                                        </>
                                    ) : isLinkedInput ? (
                                        <div className={styles.linkedPlaceholder}>
                                            <img
                                                src={LINKED_IMAGE_ICON}
                                                alt=""
                                                className={styles.linkedPlaceholderIcon}
                                                draggable={false}
                                            />
                                            <span className={styles.linkedPlaceholderLabel}>
                                                {linkedState?.imageSequenceLabel || ''}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className={styles.emptyState}>
                                            <PlusOutlined className={styles.plusIcon} />
                                            <span className={styles.placeholderText}>{card.placeholder}</span>
                                        </div>
                                    )}
                                    {previewValue ? (
                                        <div className={styles.filledCardActions}>
                                            <button
                                                type="button"
                                                className={`${styles.removeButton} ${(isLinkedPlaceholder || isLinkedInput) ? styles.removeButtonPlaceholder : ''}`}
                                                onClick={(event) => handleRemoveUpload(card.key, event)}
                                                aria-label={`删除${card.title}`}
                                            >
                                                <DeleteOutlined className={styles.removeIcon} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className={styles.sourceCardActions}>
                                            <Button
                                                size="small"
                                                className={styles.sourceActionButton}
                                                onClick={(event) => handleOpenLocalPicker(card.key, event)}
                                            >
                                                本地上传
                                            </Button>
                                        </div>
                                    )}
                                    {uploadingSlotKey === card.key ? (
                                        <div className={styles.uploadLoading} aria-label={`${card.title}上传中`}>
                                            <Spin size="small" />
                                        </div>
                                    ) : null}
                                </div>
                                <input
                                    ref={(node) => {
                                        fileInputRefs.current[card.key] = node;
                                    }}
                                    type="file"
                                    accept="image/*"
                                    className={styles.hiddenInput}
                                    onChange={(event) => handleFileChange(card.key, event)}
                                />
                            </div>
                        );
                    })}
                </div>

                <div className={styles.footer}>
                    <div className={styles.footerControls}>
                        <Select
                            value={generateCount}
                            onChange={setGenerateCount}
                            options={COUNT_OPTIONS}
                            popupMatchSelectWidth={false}
                            className={styles.countSelect}
                            variant="borderless"
                            getPopupContainer={(triggerNode) => triggerNode?.parentElement || triggerNode}
                        />
                    </div>
                    <Button
                        type="primary"
                        className={styles.generateButton}
                        onClick={canGenerate ? handleGenerate : undefined}
                        disabled={!canGenerate}
                    >
                        <span className={styles.generateButtonContent}>
                            <span>{isGenerating ? '生成中' : '生成'}</span>
                            {!isGenerating ? <img src={beanIcon} alt="" className={styles.beanIcon} draggable={false} /> : null}
                            {!isGenerating ? <span>{costBean}</span> : null}
                        </span>
                    </Button>
                </div>
            </div>
        </>
    );
};

export default ImageUpscalePanel;
